import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { MagnifyingGlass, MapPin, X as XIcon } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";

/**
 * Address autocomplete input backed by the backend's Nominatim proxy
 * (`/api/geo/search`). Debounced, keyboard-navigable, accessible.
 *
 * Props:
 *   value          string  — the displayed text
 *   onTextChange   (text)  — fires on every keystroke (lets the parent treat
 *                            the field as a normal controlled input)
 *   onSelect       ({lat,lng,address,short_label}) — fires when the user picks
 *                            a suggestion. The parent should set this as the
 *                            start/end point.
 *   placeholder    string
 *   accent         "#007AFF" | "#FF3B30"  — focus border colour
 *   country        ISO-3166-1 alpha-2, default "dk" (bias to Denmark)
 *   testId         string  — root data-testid; the input gets `${testId}-input`
 */
export default function AddressAutocomplete({
  value,
  onTextChange,
  onSelect,
  placeholder,
  accent = "#007AFF",
  country = "dk",
  testId = "address-autocomplete",
}) {
  const { lang } = useI18n();
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  // Debounced search
  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 3) {
      setResults([]);
      setLoading(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const myId = ++reqIdRef.current;
      try {
        const { data } = await api.get("/geo/search", { params: { q, limit: 6, country } });
        if (reqIdRef.current !== myId) return; // a newer query supersedes
        setResults(data.results || []);
        setHighlight(0);
      } catch {
        setResults([]);
      } finally {
        if (reqIdRef.current === myId) setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value, country]);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (r) => {
    onSelect?.({
      lat: r.lat,
      lng: r.lng,
      address: r.display_name,
      short_label: r.short_label,
    });
    onTextChange?.(r.display_name);
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => (h + 1) % results.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => (h - 1 + results.length) % results.length); }
    else if (e.key === "Enter") { e.preventDefault(); pick(results[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const clearText = () => {
    onTextChange?.("");
    setResults([]);
    inputRef.current?.focus();
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <div className="relative">
        <MagnifyingGlass
          weight="bold"
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"
        />
        <input
          ref={inputRef}
          data-testid={`${testId}-input`}
          type="text"
          value={value || ""}
          onChange={(e) => { onTextChange?.(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder || (lang === "da" ? "Søg adresse…" : "Search address…")}
          className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[color:var(--ac)] px-9 py-3 text-sm outline-none placeholder:text-zinc-600"
          style={{ "--ac": accent }}
          autoComplete="off"
          spellCheck="false"
        />
        {value && (
          <button
            type="button"
            onClick={clearText}
            data-testid={`${testId}-clear`}
            aria-label={lang === "da" ? "Ryd" : "Clear"}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-500 hover:text-white"
          >
            <XIcon weight="bold" size={14} />
          </button>
        )}
      </div>

      {open && (value || "").trim().length >= 3 && (
        <div
          role="listbox"
          data-testid={`${testId}-dropdown`}
          className="absolute z-30 left-0 right-0 mt-1 border border-[#262626] bg-[#0F0F0F] max-h-72 overflow-y-auto shadow-2xl"
        >
          {loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-zinc-500 uppercase tracking-widest">
              {lang === "da" ? "Søger…" : "Searching…"}
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-zinc-500">
              {lang === "da" ? "Ingen resultater" : "No results"}
            </div>
          )}
          {results.map((r, i) => (
            <button
              key={`${r.osm_type}-${r.osm_id}-${i}`}
              type="button"
              data-testid={`${testId}-option-${i}`}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => pick(r)}
              className={`w-full text-left px-3 py-2 flex items-start gap-2 border-b border-[#1A1A1A] last:border-b-0 transition-colors ${
                i === highlight ? "bg-[#1A1A1A]" : "hover:bg-[#1A1A1A]"
              }`}
            >
              <MapPin weight="duotone" size={16} color={accent} className="flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-sm text-white truncate">{r.short_label || r.display_name.split(",")[0]}</div>
                <div className="text-[11px] text-zinc-500 truncate">{r.display_name}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
